/**
 * Unified Workforce Dashboard
 *
 * Comprehensive view of all workforce metrics:
 * - Case management overview
 * - Recruitment pipeline
 * - Training progress
 * - Certification status
 * - Compliance tracking
 * - Task management
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Briefcase,
  BookOpen,
  Award,
  CheckCircle,
  Clock,
  AlertTriangle,
  TrendingUp,
  ArrowRight,
  ListTodo,
  Target,
  UserCheck,
  Bot,
  Shield,
  ArrowLeft,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer, PageState } from '@/components/weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';

const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

interface UnifiedStats {
  cases: {
    active: number;
    by_type: {
      recruitment: number;
      certification: number;
      training: number;
      supervision: number;
      onboarding: number;
    };
    urgent: number;
    pending_review: number;
  };
  tasks: {
    pending: number;
    overdue: number;
    completed_this_week: number;
  };
  pipeline: {
    total: number;
    by_stage: {
      applied: number;
      screening: number;
      interview: number;
      offer: number;
    };
    hired_last_30_days: number;
  };
  training: {
    total_enrollments: number;
    in_progress: number;
    completed: number;
    avg_progress: number;
  };
  compliance: {
    total_rules: number;
  };
}

interface RecentCase {
  case_id: number;
  case_number: string;
  case_type: string;
  case_status: string;
  title: string;
  priority: string;
  days_open: number;
}

export default function UnifiedDashboardPage() {
  const { t } = useAppTranslation();
  const [stats, setStats] = useState<UnifiedStats | null>(null);
  const [recentCases, setRecentCases] = useState<RecentCase[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const token = getAuthToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const [unifiedRes, casesRes] = await Promise.all([
        authFetch(getWeaveinApiUrl('/api/workforce/cases/dashboard/unified')),
        authFetch(getWeaveinApiUrl('/api/workforce/cases?limit=5')),
      ]);

      if (unifiedRes.ok) {
        const data = await unifiedRes.json();
        setStats(data);
      }

      if (casesRes.ok) {
        const data = await casesRes.json();
        setRecentCases(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const caseTypeColors: Record<string, string> = {
    recruitment: 'bg-blue-500',
    training: 'bg-purple-500',
    certification: 'bg-emerald-500',
    supervision: 'bg-orange-500',
    onboarding: 'bg-pink-500',
  };

  const statusColors: Record<string, string> = {
    open: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    pending_review: 'bg-purple-100 text-purple-700',
    resolved: 'bg-emerald-100 text-emerald-700',
    closed: 'bg-paper-2 text-ink-700',
  };

  const header = (
    <PageHeader
      eyebrow="Workforce · Overview"
      leading={
        <Link to="/workforce/dashboard" className="text-ink-400 hover:text-ink">
          <ArrowLeft className="w-5 h-5" />
        </Link>
      }
      title={t('workforce.unified.title', 'Unified Dashboard')}
      description={t('workforce.unified.subtitle', 'Complete overview of workforce operations')}
      actions={
        <Link
          to="/workforce/ai-assistant"
          className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white"
        >
          <Bot className="w-4 h-4" />
          AI Assistant
        </Link>
      }
    />
  );

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <PageContainer>
          <PageState variant="loading" />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {header}
      <PageContainer>
      <div className="space-y-6">
        {/* Top Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Link to="/workforce/cases" className="bg-surface rounded-xl p-4 border border-v2border hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <Briefcase className="w-8 h-8 text-emerald-600" />
              <span className="font-display text-2xl font-semibold text-ink">{stats?.cases.active || 0}</span>
            </div>
            <p className="text-sm text-ink-500 mt-2">Active Cases</p>
          </Link>

          <Link to="/workforce/cases" className="bg-surface rounded-xl p-4 border border-v2border hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <span className="font-display text-2xl font-semibold text-red-600">{stats?.cases.urgent || 0}</span>
            </div>
            <p className="text-sm text-ink-500 mt-2">Urgent Cases</p>
          </Link>

          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center justify-between">
              <ListTodo className="w-8 h-8 text-orange-500" />
              <span className="font-display text-2xl font-semibold text-ink">{stats?.tasks.pending || 0}</span>
            </div>
            <p className="text-sm text-ink-500 mt-2">Pending Tasks</p>
            {(stats?.tasks.overdue || 0) > 0 && (
              <p className="text-xs text-red-500 mt-1">{stats?.tasks.overdue} overdue</p>
            )}
          </div>

          <Link to="/workforce/candidates" className="bg-surface rounded-xl p-4 border border-v2border hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <Users className="w-8 h-8 text-blue-500" />
              <span className="font-display text-2xl font-semibold text-ink">{stats?.pipeline.total || 0}</span>
            </div>
            <p className="text-sm text-ink-500 mt-2">Pipeline Total</p>
          </Link>

          <Link to="/workforce/curricula" className="bg-surface rounded-xl p-4 border border-v2border hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <BookOpen className="w-8 h-8 text-purple-500" />
              <span className="font-display text-2xl font-semibold text-ink">{stats?.training.in_progress || 0}</span>
            </div>
            <p className="text-sm text-ink-500 mt-2">Training In Progress</p>
          </Link>

          <Link to="/workforce/certifications" className="bg-surface rounded-xl p-4 border border-v2border hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <Shield className="w-8 h-8 text-emerald-500" />
              <span className="font-display text-2xl font-semibold text-ink">{stats?.compliance.total_rules || 0}</span>
            </div>
            <p className="text-sm text-ink-500 mt-2">Compliance Rules</p>
          </Link>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cases by Type */}
          <div className="bg-surface rounded-xl border border-v2border">
            <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
              <h2 className="font-semibold text-ink">Cases by Type</h2>
              <Link to="/workforce/cases" className="text-sm text-emerald-600 hover:text-emerald-700">
                View All
              </Link>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {Object.entries(stats?.cases.by_type || {}).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${caseTypeColors[type] || 'bg-gray-400'}`} />
                      <span className="text-sm text-ink-700 capitalize">{type}</span>
                    </div>
                    <span className="font-medium text-ink">{count}</span>
                  </div>
                ))}
              </div>
              {stats && (
                <div className="mt-6 pt-4 border-t border-v2border-muted">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-ink-500">Pending Review</span>
                    <span className="font-medium text-purple-600">{stats.cases.pending_review}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recruitment Pipeline */}
          <div className="bg-surface rounded-xl border border-v2border">
            <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
              <h2 className="font-semibold text-ink">Recruitment Pipeline</h2>
              <Link to="/workforce/candidates" className="text-sm text-emerald-600 hover:text-emerald-700">
                View All
              </Link>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-center">
                  <p className="font-display text-2xl font-semibold text-ink">{stats?.pipeline.by_stage.applied || 0}</p>
                  <p className="text-xs text-ink-500">Applied</p>
                </div>
                <ArrowRight className="w-4 h-4 text-ink-300" />
                <div className="text-center">
                  <p className="font-display text-2xl font-semibold text-ink">{stats?.pipeline.by_stage.screening || 0}</p>
                  <p className="text-xs text-ink-500">Screening</p>
                </div>
                <ArrowRight className="w-4 h-4 text-ink-300" />
                <div className="text-center">
                  <p className="font-display text-2xl font-semibold text-ink">{stats?.pipeline.by_stage.interview || 0}</p>
                  <p className="text-xs text-ink-500">Interview</p>
                </div>
                <ArrowRight className="w-4 h-4 text-ink-300" />
                <div className="text-center">
                  <p className="font-display text-2xl font-semibold text-emerald-600">{stats?.pipeline.by_stage.offer || 0}</p>
                  <p className="text-xs text-ink-500">Offer</p>
                </div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-4 mt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-700">Hired (Last 30 days)</p>
                    <p className="font-display text-2xl font-semibold text-emerald-600">{stats?.pipeline.hired_last_30_days || 0}</p>
                  </div>
                  <UserCheck className="w-10 h-10 text-emerald-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Training Progress */}
          <div className="bg-surface rounded-xl border border-v2border">
            <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
              <h2 className="font-semibold text-ink">Training Progress</h2>
              <Link to="/workforce/curricula" className="text-sm text-emerald-600 hover:text-emerald-700">
                View All
              </Link>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="font-display text-2xl font-semibold text-purple-600">{stats?.training.in_progress || 0}</p>
                  <p className="text-xs text-purple-700">In Progress</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4 text-center">
                  <p className="font-display text-2xl font-semibold text-emerald-600">{stats?.training.completed || 0}</p>
                  <p className="text-xs text-emerald-700">Completed</p>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-ink-500">Average Progress</span>
                  <span className="text-sm font-medium text-ink">{stats?.training.avg_progress || 0}%</span>
                </div>
                <div className="bg-paper-2 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-purple-500 to-emerald-500 rounded-full h-3"
                    style={{ width: `${stats?.training.avg_progress || 0}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Cases and Tasks */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Cases */}
          <div className="bg-surface rounded-xl border border-v2border">
            <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
              <h2 className="font-semibold text-ink">Recent Cases</h2>
              <Link to="/workforce/cases" className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {recentCases.length === 0 ? (
                <div className="p-6 text-center">
                  <Briefcase className="w-10 h-10 text-ink-300 mx-auto mb-2" />
                  <p className="text-ink-500">No cases yet</p>
                  <Link to="/workforce/cases" className="text-emerald-600 hover:text-emerald-700 text-sm font-medium mt-2 inline-block">
                    Create First Case
                  </Link>
                </div>
              ) : (
                recentCases.map((caseItem) => (
                  <Link
                    key={caseItem.case_id}
                    to="/workforce/cases"
                    className="px-6 py-4 flex items-center justify-between hover:bg-paper"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${caseTypeColors[caseItem.case_type] || 'bg-gray-400'}`} />
                      <div>
                        <p className="font-medium text-ink">{caseItem.title}</p>
                        <p className="text-xs text-ink-400">{caseItem.case_number}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[caseItem.case_status] || 'bg-paper-2'}`}>
                        {caseItem.case_status.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-ink-400 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {caseItem.days_open}d
                      </span>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>

          {/* Task Summary */}
          <div className="bg-surface rounded-xl border border-v2border">
            <div className="px-6 py-4 border-b border-v2border">
              <h2 className="font-semibold text-ink">Task Summary</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <Clock className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                  <p className="font-display text-2xl font-semibold text-yellow-600">{stats?.tasks.pending || 0}</p>
                  <p className="text-xs text-yellow-700">Pending</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="font-display text-2xl font-semibold text-red-600">{stats?.tasks.overdue || 0}</p>
                  <p className="text-xs text-red-700">Overdue</p>
                </div>
                <div className="text-center p-4 bg-emerald-50 rounded-lg">
                  <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="font-display text-2xl font-semibold text-emerald-600">{stats?.tasks.completed_this_week || 0}</p>
                  <p className="text-xs text-emerald-700">This Week</p>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-v2border-muted">
                <h3 className="text-sm font-medium text-ink-700 mb-3">Quick Actions</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Link
                    to="/workforce/cases"
                    className="flex items-center justify-center p-3 bg-paper rounded-lg hover:bg-paper-2 text-sm text-ink-700"
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    New Case
                  </Link>
                  <Link
                    to="/workforce/candidates"
                    className="flex items-center justify-center p-3 bg-paper rounded-lg hover:bg-paper-2 text-sm text-ink-700"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Add Candidate
                  </Link>
                  <Link
                    to="/workforce/ai-assistant"
                    className="flex items-center justify-center p-3 bg-paper rounded-lg hover:bg-paper-2 text-sm text-ink-700"
                  >
                    <Bot className="w-4 h-4 mr-2" />
                    AI Assistant
                  </Link>
                  <Link
                    to="/workforce/skills-gap"
                    className="flex items-center justify-center p-3 bg-paper rounded-lg hover:bg-paper-2 text-sm text-ink-700"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Skills Analysis
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </PageContainer>
    </div>
  );
}
